import styled, {
  css,
  DefaultTheme,
  keyframes,
  ThemedStyledProps,
} from 'styled-components';
import { layout, media, colorUtils } from '@prisjakt/ui-base';

import Clickable from '@prisjakt/ui-clickable';
import { TitleSmallText } from '@prisjakt/ui/text/variants';
import CloseIcon from 'mdi-react/CloseIcon';
import SearchIconComponent from 'mdi-react/SearchIcon';
import { zindex } from 'src/ui/zindex';

const { spacing050, spacing100 } = layout.measurements;

const BUTTON_MIN = '40px';
const BUTTON_MAX = '135px';

const scaleIn = keyframes`
  from {
    max-width: ${BUTTON_MIN};
  }

  to {
    max-width: ${BUTTON_MAX};
  }
`;

const scaleOut = keyframes`
  from {
    max-width: ${BUTTON_MAX};
  }

  to {
    max-width: ${BUTTON_MIN};
  }
`;

type SearchButtonColorProps = {
  disabled: boolean;
  active: boolean;
};

const searchButtonColor = (
  p: ThemedStyledProps<SearchButtonColorProps, any>
) => {
  if (!p.active) {
    return '';
  }

  return `background-color: ${
    p.disabled ? 'transparent' : p.theme.colors.yellove500
  };`;
};

export const SearchButton = styled.button<{
  disabled: boolean;
  active: boolean;
}>(
  p => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background-color: ${p.theme.colors.yellove500};
    height: 100%;
    padding: 0 ${spacing100};
    user-select: none;
    cursor: ${p.disabled ? 'initial' : 'pointer'};
    width: ${BUTTON_MIN};
    min-width: ${BUTTON_MIN};
    transition: background-color 150ms ease;
    overflow: hidden;
    color: rgba(0, 0, 0, 1);

    &:disabled {
      color: rgba(0, 0, 0, 0.3);
    }

    &:hover {
      background-color: ${colorUtils.shadeColor(
        p.theme.colors.yellove500,
        p.disabled ? 0 : -10
      )};
    }

    ${media.mediumDown`
      padding: 0 ${spacing050};
    `}

    ${media.smallOnly`
    ${searchButtonColor(p)}
    `};

    ${media.mediumUp`
      ${p.disabled &&
        css`
          animation: ${scaleOut} 150ms;
          width: ${BUTTON_MIN};
        `}

      ${!p.disabled &&
        css`
          max-width: ${BUTTON_MAX};
          width: auto;
          animation: ${scaleIn} 150ms;
        `}
    `};
  `
);

export const StyledSearchBar = styled.div<{
  $active: boolean;
}>(
  p => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${layout.searchBar.height.desktop.rem};
    overflow: hidden;
    position: relative;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    background: ${p.theme.colors.base0};
    z-index: ${zindex.menu.searchSuggestions};

    ${media.smallOnly`
      box-shadow: none;
    `};

    ${SearchButton} {
      ${media.smallOnly`
        border-radius: ${p.$active ? '4px' : 0};
      `};
    }
  `
);

export const InputWrapper = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  height: 100%;
  opacity: 1;

  ${media.smallOnly`
    flex: 1 1 auto;
  `};
`;

export const SearchInput = styled.input`
  background: transparent;
  border: none;
  width: 100%;
  height: ${layout.searchBar.height.desktop.rem};
  flex-grow: 1;
  padding: 0 ${spacing100};

  ${media.smallOnly`
    height: 48px;
  `};

  &:focus {
    outline: none;
  }
`;

export const ClearIcon = styled(CloseIcon)``;

type ClearButtonProps = {
  show?: boolean;
};

export const ClearButton = styled(Clickable)<ClearButtonProps>`
  display: ${p => (p.show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.bg.tertiary};
  color: ${p => p.theme.icons.primary};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  margin-right: ${spacing100};
  cursor: pointer;
  transition: background-color 150ms ease;
  flex: 0 0 auto;

  &:hover {
    background-color: ${p => colorUtils.shadeColor(p.theme.bg.tertiary, -20)};
  }
`;

export const SearchIcon = styled(SearchIconComponent)``;

export const SearchButtonText = styled(TitleSmallText)`
  > * {
    align-items: center;
    justify-content: center;
  }
`;

export const Text = styled.span<{ $visible: boolean }>`
  display: ${p => (p.$visible ? 'inline' : 'none')};

  ${media.smallOnly`
    display: none;
  `};
`;

type FocusedProp = { $focused: boolean; $visible: boolean };

const searchBarMobile = (p: ThemedStyledProps<FocusedProp, DefaultTheme>) => {
  if (p.$focused) {
    return css`
      ${media.smallOnly`
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        position: fixed;
        z-index: ${zindex.menu.searchBar};
        top: 0;
        left: 0;
        right: 0;
        height: 56px;
        background: ${p.theme.bg.primary};
        flex-direction: column;
        height: 100vh;
        max-width: initial;
      `}
    `;
  }

  return null;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

export const SearchBarWrapper = styled.div<FocusedProp>`
  position: relative;
  width: 100%;
  justify-content: center;
  max-width: 590px;
  display: flex;
  ${p => (p.$focused ? 'z-index: 109;' : 'z-index:1;')}
  ${p => css`
    animation: ${p.$visible ? fadeIn : fadeOut} 250ms;
    animation-fill-mode: forwards;
  `}
  ${p => searchBarMobile(p)}
`;

export const SearchBarGroup = styled.div<{ $active: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  flex-shrink: 0;
  ${p => media.smallOnly`
    ${
      p.$active
        ? 'box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2); max-width: 100%; height: 56px;'
        : ''
    }
    padding: 0 ${spacing050};
    z-index: ${zindex.modal};
  `};
`;

export const CloseButton = styled(Clickable)<{ $visible: boolean }>`
  height: 100%;
  display: none;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.text.primary};
  ${p => media.smallOnly`
    ${!p.$visible ? 'display: none;' : 'display: flex;'}
  `}
`;
